import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import TopBar from './TopBar';
import SharePopup from './SharePopup'; // Import SharePopup
import FunderAcknowledgment from './common/FunderAcknowledgment';
import { Home, Folder, BookOpen, Heart, BookMarked, Calendar } from 'lucide-react';
import { NavLink } from 'react-router-dom';

const bottomMenu = [
  { to: '/', label: 'Home', icon: <Home /> },
  { to: '/resources', label: 'Resources', icon: <BookOpen /> },
  { to: '/read-along', label: 'Read-Along', icon: <BookMarked /> },
  { to: '/projects', label: 'Projects', icon: <Folder />, enabled: false },
  { to: '/donate', label: 'Donate', icon: <Heart />, enabled: false },
];

const Layout = () => {
  const location = useLocation();
  
  // Centralized state for share popup properties
  const [sharePopupProps, setSharePopupProps] = useState(null);
  const [pageShareUrl, setPageShareUrl] = useState(window.location.href);

  // State for other modals/UI
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [topicSheetOpen, setTopicSheetOpen] = useState(false);
  const [showBottomNav, setShowBottomNav] = useState(true);
  const lastScrollY = React.useRef(0);

  // Hide nav on login pages, admin pages and dedicated user app pages
  const isLoginPage = location.pathname === '/login' || location.pathname === '/user-login';
  const isAdminPage = location.pathname.startsWith('/admin');
  const isUserAppPage = location.pathname.startsWith('/teacher');
  const isWorksheetPage = /\/(marathi|english|matra-setu)\/[^/]+\/worksheet/.test(location.pathname);
  const isBingoPrintPage = location.pathname === '/english/sight-words/bingo/print';
  // Full-bleed, non-scrolling "stage" layout (WordRaceStage — a big edge-to-edge tap
  // zone with controls anchored to the viewport bottom), same reason /vaachan-katta's
  // own Marathi Word Race already sits in isEditorialPage below — the global fixed
  // bottom nav bar would otherwise sit on top of and hide its Hear/Stop buttons.
  const isWordRacePage = location.pathname === '/english/sight-words/word-race';
  const isLpPrintPreview = location.pathname.startsWith('/print/marathi/lp-preview');
  // Hide platform chrome on all editorial v2 program-index pages.
  // /marathi, /maths, /english are now v2 by default. /v2 aliases stay for back-compat.
  // /esl + /esl/v2 redirect to /english upstream — gate them anyway in case the redirect frame briefly renders.
  const isV2Index = /^\/(marathi|maths|english|esl)(\/v2)?$/.test(location.pathname);
  const isPadhoJalnaHome  = location.pathname === '/';
  const isEditorialPage   = location.pathname.startsWith('/annual-reports') || location.pathname.startsWith('/vaachan-katta');
  const isPJNavPage = ['/camp-hub', '/oldhome', '/english/sessions', '/programs', '/matra-setu', '/about', '/about/'].includes(location.pathname);
  const hideShell = isLoginPage || isAdminPage || isUserAppPage || isWorksheetPage || isBingoPrintPage || isWordRacePage || isLpPrintPreview || isV2Index || isPadhoJalnaHome || isEditorialPage || isPJNavPage;

  // Auto-hide bottom nav on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (filterModalOpen || sharePopupProps || topicSheetOpen) return; // Don't hide/show if a modal is open
      const currentY = window.scrollY;
      if (currentY > lastScrollY.current && currentY > 40) {
        setShowBottomNav(false); // Hide on scroll down
      } else {
        setShowBottomNav(true); // Show on scroll up
      }
      lastScrollY.current = currentY;
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [filterModalOpen, sharePopupProps, topicSheetOpen]);

  // Hide bottom nav when any modal is open
  useEffect(() => {
    if (filterModalOpen || sharePopupProps || topicSheetOpen) {
      setShowBottomNav(false);
    } else if (!filterModalOpen && !sharePopupProps && !topicSheetOpen) {
      // Only show if no modal is open; scroll handler will manage from there
      setShowBottomNav(true);
    }
  }, [filterModalOpen, sharePopupProps, topicSheetOpen]);

  // Handler for sharing the current page from the TopBar
  const handlePageShare = () => {
    setSharePopupProps({
      url: pageShareUrl, // Uses the URL that is passively updated by App.js
      title: 'Share this page',
      mode: 'page',
    });
  };
  
  // Handler to close the share popup
  const handleCloseShare = () => {
    setSharePopupProps(null);
  };

  return (
    <>
      {/* TopBar - Hidden on login pages, admin pages and user app pages */}
      {!hideShell && <TopBar onShareClick={handlePageShare} />}
      
      {/* Desktop navigation bar - Hidden on login pages, admin pages and user app pages */}
      {!hideShell && (
        <nav className="sticky top-14 z-40 hidden sm:flex w-full bg-white md-elevation-1 border-b border-gray-200/60 px-4 py-2 flex-row items-center gap-0.5 md:gap-1 lg:gap-1.5 justify-center max-w-4xl mx-auto">
        {[
          { to: '/', label: 'Home', activeClass: 'bg-yellow-100 text-yellow-800', hoverClass: 'hover:bg-yellow-50', enabled: true },
          { to: '/camp-hub', label: 'Camps', activeClass: 'bg-blue-100 text-blue-800', hoverClass: 'hover:bg-blue-50', enabled: true },
          { to: '/read-along', label: 'Read-Along', activeClass: 'bg-purple-100 text-purple-800', hoverClass: 'hover:bg-purple-50', enabled: true },
          { to: '/resources', label: 'Resources', activeClass: 'bg-green-100 text-green-800', hoverClass: 'hover:bg-green-50', enabled: true },
          { to: '/projects', label: 'Projects', enabled: false },
          { to: '/donate', label: 'Donate', enabled: false },
        ].map(item => (
          item.enabled ? (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `md-button md-button-text md-ripple relative px-3 py-1.5 rounded-full md-title-small transition-all duration-200 outline-none touch-manipulation min-h-[40px] flex items-center justify-center font-medium
                ${isActive 
                  ? `${item.activeClass} md-elevation-1 shadow-sm` 
                  : `text-gray-700 ${item.hoverClass} hover:text-gray-900`
                }`
              }
              tabIndex={0}
              aria-label={item.label}
            >
              {item.label}
            </NavLink>
          ) : (
            <span
              key={item.to}
              className="relative px-3 py-1.5 rounded-full md-title-small min-h-[40px] flex items-center justify-center text-gray-400 bg-gray-50 cursor-not-allowed select-none border border-dashed border-gray-200 opacity-60"
              tabIndex={-1}
              aria-disabled="true"
            >
              {item.label}
            </span>
          )
        ))}
      </nav>
      )}

      <main className={hideShell ? "" : "pb-20 sm:pb-0"}>
        <Outlet context={{ setSharePopupProps, setPageShareUrl, filterModalOpen, setFilterModalOpen, topicSheetOpen, setTopicSheetOpen }} />
        {!hideShell && <FunderAcknowledgment />}
      </main>

      {/* Render SharePopup when props are set */}
      {sharePopupProps && (
        <SharePopup
          {...sharePopupProps}
          onClose={handleCloseShare}
        />
      )}
      
      {/* Bottom nav - Hidden on login pages, admin pages and user app pages */}
      {!hideShell && (
        <nav
          className={`fixed bottom-0 left-0 w-full z-50 sm:hidden transition-transform duration-300 ${showBottomNav ? 'translate-y-0' : 'translate-y-full'}`}
          style={{ boxShadow: '0 -2px 16px 0 rgba(0,0,0,0.08)' }}
          aria-label="Site navigation"
        >
        <div className="flex justify-around bg-white rounded-t-2xl py-1 px-2 md-bottom-nav">
          <NavLink
            to="/"
            className={({ isActive }) =>
              `md-bottom-nav-item touch-manipulation ${isActive ? 'active' : ''}`
            }
            aria-label="Home"
          >
            <Home size={20} />
            <span className="md-bottom-nav-item-label">Home</span>
          </NavLink>
          <NavLink
            to="/camp-hub"
            className={({ isActive }) =>
              `md-bottom-nav-item touch-manipulation ${isActive ? 'active' : ''}`
            }
            aria-label="Camps"
          >
            <Calendar size={20} />
            <span className="md-bottom-nav-item-label">Camps</span>
          </NavLink>
          <NavLink
            to="/read-along"
            className={({ isActive }) =>
              `md-bottom-nav-item touch-manipulation ${isActive ? 'active' : ''}`
            }
            aria-label="Read-Along"
          >
            <BookMarked size={20} />
            <span className="md-bottom-nav-item-label">Read-Along</span>
          </NavLink>
          <NavLink
            to="/resources"
            className={({ isActive }) =>
              `md-bottom-nav-item touch-manipulation ${isActive ? 'active' : ''}`
            }
            aria-label="Resources"
          >
            <BookOpen size={20} />
            <span className="md-bottom-nav-item-label">Resources</span>
          </NavLink>
        </div>
      </nav>
      )}
    </>
  );
};

export default Layout;
